import { createContext, ReactElement, useEffect, useReducer } from 'react';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import service from 'utils/service';
import { UserProfile } from 'types/user-profile';
import { clearAddressProfileSession } from 'utils/address/addressProfile';

import { InitialLoginContextProps, JWTContextType } from 'types/auth';

// constant
const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

function setSession(token?: string | null): void {
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
    localStorage.removeItem('loginCustomer');
  }
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export function JWTProvider({ children }: { children: ReactElement }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const authToken = window.localStorage.getItem('authToken');
        const storedCustomer = window.localStorage.getItem('loginCustomer');
        const user = storedCustomer ? JSON.parse(storedCustomer)?.[0] : null;

        if (authToken) {
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: user || null
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (username: string, password: string) => {
    const response: any = await service.makeAPICall({
      methodName: service.Methods.POST,
      apiUrl: service.API_URL.customerAuth.login,
      body: { username, password }
    });

    if (response?.code !== 200) {
      throw new Error(response?.message || 'Unable to login. Please try again.');
    }

    const customerData = response?.data || [];
    const token = customerData?.[0]?.token;
    if (!token) {
      throw new Error('Login succeeded but token was missing in response.');
    }

    window.localStorage.setItem('loginCustomer', JSON.stringify(customerData));
    setSession(token);

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: (customerData?.[0] || null) as UserProfile | null
      }
    });
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    mobileNo?: string,
    firmName?: string,
    address?: string,
    pincodeId?: number,
    city?: string,
    stateId?: string
  ) => {
    const body = {
      NAME: `${firstName} ${lastName}`.trim(),
      MOBILE_NO: mobileNo || '',
      EMAIL_ID: email,
      FIRM_NAME: firmName || '',
      PASSWORD: password,
      ADDRESS: address?.trim() || '',
      CITY: city || '',
      PINCODE_ID: pincodeId ?? '',
      STATE_ID: stateId || ''
    };

    const response: any = await service.makeAPICall({
      methodName: service.Methods.POST,
      apiUrl: service.API_URL.auth.signup,
      body
    });

    if (response?.code !== 200 && response?.success !== true) {
      const registerError: any = new Error(response?.message || 'Unable to register. Please try again.');
      registerError.code = response?.code;
      throw registerError;
    }
  };

  const resetPassword = async (username: string) => {
    const response: any = await service.makeAPICall({
      methodName: service.Methods.POST,
      apiUrl: service.API_URL.passwordreset.reset,
      body: { username }
    });

    if (response?.code !== 200) {
      throw new Error(response?.message || 'Unable to send OTP. Please try again.');
    }

    window.localStorage.setItem('otpUsername', username);
  };

  const codeVerification = async (verificationCode: string) => {
    const username = window.localStorage.getItem('otpUsername');
    if (!username) {
      throw new Error('Reset session expired. Please request OTP again.');
    }

    const response: any = await service.makeAPICall({
      methodName: service.Methods.POST,
      apiUrl: service.API_URL.passwordreset.verifyotp,
      body: {
        otp: verificationCode,
        username
      }
    });

    if (response?.code !== 200) {
      throw new Error(response?.message || 'Invalid OTP. Please try again.');
    }
  };

  const updatePassword = async (password: string) => {
    const username = window.localStorage.getItem('otpUsername');
    if (!username) {
      throw new Error('Reset session expired. Please request OTP again.');
    }

    const response: any = await service.makeAPICall({
      methodName: service.Methods.POST,
      apiUrl: service.API_URL.passwordreset.updatePassword,
      body: {
        username,
        newPassword: password
      }
    });

    if (response?.code !== 200) {
      throw new Error(response?.message || 'Unable to update password. Please try again.');
    }

    window.localStorage.removeItem('otpUsername');
  };

  const logout = () => {
    setSession(null);
    clearAddressProfileSession();
    dispatch({ type: LOGOUT });
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{ ...state, login, logout, register, resetPassword, codeVerification, updatePassword, updateProfile }}
    >
      {children}
    </JWTContext.Provider>
  );
}

export default JWTContext;
